'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import {
  TrendingUp, CheckCircle,
  Filter, Calendar, BarChart2, LayoutGrid,
  Zap, Mail, LifeBuoy, Smartphone,
  Info, MessageCircle, FileText, Shield, AlertTriangle,
  Megaphone, HelpCircle, Clock, Heart,
  type LucideIcon,
} from 'lucide-react';
import { subscribeNewsletter } from '@/lib/api-client';
import { useAuth } from '@/context/AuthContext';
import { useSite } from '@/context/SiteContext';

const footerLinks: {
  stocks: { label: string; href: string; icon: LucideIcon }[];
  services: { label: string; href: string; icon: LucideIcon }[];
  company: { label: string; href: string; icon: LucideIcon; iconClass?: string }[];
} = {
  stocks: [
    { label: 'Stock Screener',    href: '/stocks/screener', icon: Filter },
    { label: 'Earnings Calendar', href: '/stocks/earnings', icon: Calendar },
    { label: 'Market Movers',     href: '/markets/gainers', icon: BarChart2 },
    { label: 'Heatmap',           href: '/heatmap', icon: LayoutGrid },
  ],
  services: [
    { label: 'Pro Plan',    href: '/pro',        icon: Zap },
    { label: 'Newsletter',  href: '/newsletter', icon: Mail },
    { label: 'Support',     href: '#chat',       icon: LifeBuoy },
    { label: 'Mobile App',  href: '/mobile',     icon: Smartphone },
  ],
  company: [
    { label: 'About',            href: '/about',       icon: Info },
    { label: 'Contact',          href: '#contact',     icon: MessageCircle },
    { label: 'Terms of Service', href: '/terms',       icon: FileText },
    { label: 'Privacy Policy',   href: '/privacy',     icon: Shield },
    { label: 'Data Disclaimer',  href: '/disclaimer',  icon: AlertTriangle },
    { label: 'Advertise',        href: '/advertise',   icon: Megaphone },
    { label: 'FAQ',              href: '/faq',         icon: HelpCircle },
    { label: 'Changelog',        href: '/changelog',   icon: Clock },
    { label: 'Donate',           href: '/donate',      icon: Heart, iconClass: 'text-rose-500' },
  ],
};

function FooterSubscribe() {
  const { user } = useAuth();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'done'>('idle');

  const handleSubscribe = async () => {
    const addr = email.trim() || user?.email;
    if (!addr) return;
    setStatus('loading');
    try {
      await subscribeNewsletter(addr, user?.id);
      setStatus('done');
    } catch {
      setStatus('idle');
    }
  };

  if (status === 'done') {
    return (
      <div className="flex items-center gap-2 text-emerald-400 text-sm">
        <CheckCircle size={14} />
        You're subscribed!
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 w-full lg:w-auto">
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder={user?.email ?? 'Enter your email'}
        className="bg-card-bg border border-default rounded-lg px-3 py-2 text-sm text-slate-300 placeholder-slate-600 focus:outline-none focus:border-sky-500 w-52"
        onKeyDown={(e) => e.key === 'Enter' && handleSubscribe()}
      />
      <button
        onClick={handleSubscribe}
        disabled={status === 'loading'}
        className="px-3 py-2 bg-sky-500 hover:bg-sky-600 disabled:opacity-60 text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap"
      >
        {status === 'loading' ? '…' : 'Subscribe'}
      </button>
    </div>
  );
}

export default function Footer() {
  const { siteName, logoUrl } = useSite();

  return (
    <footer className="bg-surface border-t border-default">
      <div className="max-w-screen-2xl mx-auto px-2 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {/* Column 1: Brand */}
          <div>
            <Link href="/" className="flex items-center gap-2 mb-4">
              {logoUrl ? (
                <img src={logoUrl} alt={siteName} className="h-8 w-auto object-contain" />
              ) : (
                <>
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-400 to-sky-500 flex items-center justify-center">
                    <TrendingUp size={18} className="text-white" />
                  </div>
                  <span className="font-bold text-lg bg-gradient-to-r from-emerald-400 to-sky-400 bg-clip-text text-transparent">
                    {siteName}
                  </span>
                </>
              )}
            </Link>
            <p className="text-fg-subtle text-sm leading-relaxed mb-6">
              Free financial data for self-directed investors. Real-time quotes, charts, and analysis tools.
            </p>
            {/* App store badges */}
            <div className="flex flex-col gap-2">
              <a
                href="/mobile"
                className="flex items-center gap-3 bg-card-bg hover:bg-card-hover-bg border border-default rounded-xl px-4 py-2.5 transition-colors w-fit"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" className="text-slate-300">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                </svg>
                <div>
                  <div className="text-[10px] text-fg-subtle">Download on the</div>
                  <div className="text-sm font-semibold text-slate-200">App Store</div>
                </div>
              </a>
              <a
                href="/mobile"
                className="flex items-center gap-3 bg-card-bg hover:bg-card-hover-bg border border-default rounded-xl px-4 py-2.5 transition-colors w-fit"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" className="text-slate-300">
                  <path d="M3 20.5v-17c0-.83.94-1.3 1.6-.8l14 8.5c.6.36.6 1.24 0 1.6l-14 8.5c-.66.5-1.6.03-1.6-.8z" />
                </svg>
                <div>
                  <div className="text-[10px] text-fg-subtle">Get it on</div>
                  <div className="text-sm font-semibold text-slate-200">Google Play</div>
                </div>
              </a>
            </div>
          </div>

          {/* Column 2: Stocks */}
          <div>
            <h3 className="text-slate-200 font-semibold text-sm mb-4">Stocks</h3>
            <ul className="space-y-2.5">
              {footerLinks.stocks.map(({ label, href, icon: Icon }) => (
                <li key={href}>
                  <Link href={href} className="flex items-center gap-2 text-fg-subtle hover:text-fg text-sm transition-colors group">
                    <Icon size={13} className="flex-shrink-0 text-fg-muted group-hover:text-slate-300 transition-colors" />
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Services */}
          <div>
            <h3 className="text-slate-200 font-semibold text-sm mb-4">Services</h3>
            <ul className="space-y-2.5">
              {footerLinks.services.map(({ label, href, icon: Icon }) => (
                <li key={label}>
                  {href === '#chat' ? (
                    <button onClick={() => window.dispatchEvent(new CustomEvent('open-chat'))}
                      className="flex items-center gap-2 text-fg-subtle hover:text-fg text-sm transition-colors group">
                      <Icon size={13} className="flex-shrink-0 text-fg-muted group-hover:text-slate-300 transition-colors" />
                      {label}
                    </button>
                  ) : (
                    <Link href={href} className="flex items-center gap-2 text-fg-subtle hover:text-fg text-sm transition-colors group">
                      <Icon size={13} className="flex-shrink-0 text-fg-muted group-hover:text-slate-300 transition-colors" />
                      {label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4: Company */}
          <div>
            <h3 className="text-slate-200 font-semibold text-sm mb-4">Company</h3>
            <ul className="space-y-2.5">
              {footerLinks.company.map(({ label, href, icon: Icon, iconClass }) => {
                const onAction = href === '#chat'
                  ? () => window.dispatchEvent(new CustomEvent('open-chat'))
                  : href === '#contact'
                    ? () => window.dispatchEvent(new CustomEvent('open-contact', { detail: { topic: 'general' } }))
                    : null;
                return (
                  <li key={label}>
                    {onAction ? (
                      <button onClick={onAction}
                        className="flex items-center gap-2 text-fg-subtle hover:text-fg text-sm transition-colors group">
                        <Icon size={13} className={`flex-shrink-0 transition-colors ${iconClass ?? 'text-fg-muted group-hover:text-slate-300'}`} />
                        {label}
                      </button>
                    ) : (
                      <Link href={href} className="flex items-center gap-2 text-fg-subtle hover:text-fg text-sm transition-colors group">
                        <Icon size={13} className={`flex-shrink-0 transition-colors ${iconClass ?? 'text-fg-muted group-hover:text-slate-300'}`} />
                        {label}
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-default pt-8 flex flex-col lg:flex-row items-start lg:items-center gap-6 justify-between">
          <p className="text-slate-600 text-xs max-w-xl leading-relaxed">
            &copy; 2025 {siteName}. For informational purposes only. Not investment advice.
            Data may be delayed. {siteName} is not a registered investment advisor.
            Please do your own due diligence before making any investment decisions.
          </p>
          {/* Newsletter signup */}
          <FooterSubscribe />
        </div>
      </div>
    </footer>
  );
}
